import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, PhoneCall, ClipboardList, Truck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { api, formatApiError } from "../../lib/api";
import { BOOKING_SERVICES, LABOR, TIME_WINDOWS, slug } from "../../lib/data";
import { Reveal, Eyebrow } from "./Reveal";
import { BookingSuccess } from "./BookingSuccess";

const EMPTY = { name: "", phone: "", email: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", address: "", preferred_date: "", time_window: "", notes: "" };
const STEPS = [
  { icon: ClipboardList, t: "Send your request", d: "Pick the service, your vehicle, where it's parked and when works for you." },
  { icon: PhoneCall, t: "We call to confirm", d: "We call you to lock in the time, the address and a written quote." },
  { icon: Truck, t: "We pull up & wrench", d: "The shop comes to you. No tow, no waiting room." },
];

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${on ? "border-transparent bg-neon text-white" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</Label>
    {children}
  </div>
);

const inputCls = "h-12 rounded-xl border-white/10 bg-black/40 text-base text-white placeholder:text-zinc-500 focus-visible:ring-[var(--mag)]";

export const Booking = ({ services, setServices }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (s) => setServices((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!services.length) return toast.error("Pick at least one service.");
    if (!form.time_window) return toast.error("Pick a preferred time window.");
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid phone number so we can call you.");
    setLoading(true);
    try {
      const { data } = await api.createBooking({ ...form, services });
      setDone({ ...form, services, id: data?.id });
      setForm(EMPTY);
      setServices([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="relative border-t border-white/10 bg-[#0a0a10] py-28 lg:py-40">
      <div className="absolute left-1/2 top-0 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-[var(--mag)]/10 blur-[160px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow num="04">Book</Eyebrow>
            <h2 className="font-race mt-6 text-6xl leading-[0.9] text-white sm:text-7xl">Book it.<br /><span className="text-neon">We'll call.</span></h2>
            <p className="mt-6 max-w-md text-base text-zinc-300 md:text-lg">Every appointment gets a confirmation call from {"248-820-9599"} before we roll out.</p>
          </Reveal>
          <div className="mt-12 space-y-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08} className="flex gap-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-[var(--surface)]"><s.icon className="h-5 w-5 text-[var(--mag)]" /></span>
                <div>
                  <div className="font-race text-2xl text-white"><span className="font-mono-x mr-2 text-sm text-zinc-400">0{i + 1}</span>{s.t}</div>
                  <p className="mt-1 text-sm text-zinc-300">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="clip-race relative mt-12 hidden aspect-[16/10] overflow-hidden lg:block">
            <img src="/img/mech4.webp" alt="Suspension repair underneath a car" className="h-full w-full object-cover grayscale" />
            <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(139,44,255,0.35),rgba(255,43,214,0.2))] mix-blend-color" />
          </Reveal>
        </div>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} data-testid="booking-form" className="space-y-8 rounded-3xl border border-white/10 bg-[var(--surface)]/80 p-6 backdrop-blur-xl sm:p-10">
            <Field label="Service needed">
              <div className="flex flex-wrap gap-2">
                {BOOKING_SERVICES.map((s) => <Chip key={s} on={services.includes(s)} onClick={() => toggle(s)} testid={`booking-service-${slug(s)}`}>{s}</Chip>)}
              </div>
              <details className="group mt-3">
                <summary className="cursor-pointer text-sm font-semibold text-[var(--pink-text)]">+ Specific repairs from the labor guide</summary>
                <div className="mt-3 flex flex-wrap gap-2">
                  {LABOR.map((l) => <Chip key={l.name} on={services.includes(l.name)} onClick={() => toggle(l.name)} testid={`booking-repair-${slug(l.name)}`}>{l.name}</Chip>)}
                </div>
              </details>
            </Field>
            <div className="grid gap-5 sm:grid-cols-3">
              <Field label="Year"><Input required value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2016" inputMode="numeric" className={inputCls} data-testid="booking-vehicle-year-input" /></Field>
              <Field label="Make"><Input required value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Chevrolet" className={inputCls} data-testid="booking-vehicle-make-input" /></Field>
              <Field label="Model"><Input required value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Malibu" className={inputCls} data-testid="booking-vehicle-model-input" /></Field>
            </div>
            <Field label="Service address"><Input required value={form.address} onChange={set("address")} placeholder="Street, city, ZIP" className={inputCls} data-testid="booking-address-input" /></Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Preferred date"><Input required type="date" min={today} value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="booking-date-input" /></Field>
              <Field label="Time window">
                <div className="grid grid-cols-2 gap-2">
                  {TIME_WINDOWS.map((t) => (
                    <button type="button" key={t.value} data-testid={`booking-time-${t.value}`} onClick={() => setForm((f) => ({ ...f, time_window: t.value }))} className={`h-12 rounded-xl border text-sm font-semibold transition-colors ${form.time_window === t.value ? "border-transparent bg-neon text-white" : "border-white/10 bg-black/40 text-zinc-300 hover:border-white/30"}`}>{t.label}</button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your name"><Input required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="booking-name-input" /></Field>
              <Field label="Phone (we'll call this)"><Input required type="tel" value={form.phone} onChange={set("phone")} placeholder="(248) 555-0123" className={inputCls} data-testid="booking-phone-input" /></Field>
            </div>
            <Field label="What's going on? (optional)"><Textarea value={form.notes} onChange={set("notes")} rows={4} placeholder="Noises, warning lights, anything we should know…" className="rounded-xl border-white/10 bg-black/40 text-base text-white placeholder:text-zinc-500" data-testid="booking-notes-input" /></Field>
            <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={loading} data-testid="booking-form-submit-button" className="btn-neon flex w-full items-center justify-center gap-3 rounded-full py-5 text-sm font-bold uppercase tracking-[0.2em] text-white disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {loading ? "Sending…" : "Request appointment"}
            </motion.button>
            <p className="text-center text-xs text-zinc-400">Your appointment isn't final until we call you to confirm.</p>
          </form>
        </Reveal>
      </div>
      <BookingSuccess booking={done} onClose={() => setDone(null)} />
    </section>
  );
};
